import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useReferralSummary } from '@/app/components/ReferralPageComponents/hooks/useReferralSummary';
import MezzaninePointsSection from '@/app/components/ReferralPageComponents/MezzaninePointsSection';
import ReferralAnalytics from '@/app/components/ReferralPageComponents/ReferralAnalytics';
import ReferralCreateCode from '@/app/components/ReferralPageComponents/ReferralCreateCode';
import ReferralHero from '@/app/components/ReferralPageComponents/ReferralHero';
import ReferralInvitedUsers from '@/app/components/ReferralPageComponents/ReferralInvitedUsers';
import ReferralInviteeStatus from '@/app/components/ReferralPageComponents/ReferralInviteeStatus';
import ReferralPageContainer from '@/app/components/ReferralPageComponents/ReferralPageContainer';
import { DASH } from '@/app/shared/constants/common';
import { formatPoints } from '@/app/shared/lib/formatters';
import { Tabs } from '@/app/shared/ui/Tabs';

const REFERRAL_TABS = [
  { id: 'points' as const, content: 'Points' },
  { id: 'bindings' as const, content: 'Referrals' },
];

type ReferralTab = (typeof REFERRAL_TABS)[number]['id'];

const ReferralPage = () => {
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code');
  const [activeTab, setActiveTab] = useState<ReferralTab>(codeFromUrl ? 'bindings' : 'points');
  const {
    invitedUsers,
    activeInvitees,
    referrerTotalEarned,
    referredByCode,
    inviteeActiveBoost: inviteeActiveBoostFromApi,
    isLoading,
  } = useReferralSummary();

  useEffect(() => {
    if (!codeFromUrl) return;
    setActiveTab('bindings');
  }, [codeFromUrl]);

  const earningsLabel =
    !isLoading && referrerTotalEarned != null && referrerTotalEarned !== ''
      ? `${formatPoints(referrerTotalEarned)} Points`
      : DASH;

  const inviteeActiveBoost = isLoading
    ? DASH
    : referredByCode && inviteeActiveBoostFromApi
      ? `Active (${inviteeActiveBoostFromApi})`
      : DASH;

  return (
    <ReferralPageContainer
      hero={<ReferralHero />}
      tabs={
        <Tabs
          tabs={REFERRAL_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          className='flex items-end gap-5'
          tabClassName='-mb-px border-b-2 border-transparent pb-5 text-base leading-5 transition-colors'
          activeTabClassName='border-brand-1100 text-brand-1100'
          inactiveTabClassName='text-gray-400 hover:text-brand-1100'
        />
      }
    >
      <div className='flex flex-col gap-8 px-0 pb-10 md:gap-10'>
        {activeTab === 'bindings' && (
          <>
            <div className='flex flex-col gap-4 px-5 pt-8 md:gap-4 md:px-10 md:pt-10'>
              <ReferralInviteeStatus />
              <ReferralCreateCode />
            </div>

            <div className='px-5 md:px-10'>
              <ReferralAnalytics
                inviteeActiveBoost={inviteeActiveBoost}
                referrerTotalEarnings={earningsLabel}
                invitedUsers={isLoading ? DASH : String(invitedUsers)}
                activeInvitees={isLoading ? DASH : String(activeInvitees)}
              />
            </div>

            <div className='px-5 md:px-10'>
              <ReferralInvitedUsers />
            </div>
          </>
        )}

        {activeTab === 'points' && (
          <div className='px-5 pt-8 md:px-10 md:pt-10'>
            <MezzaninePointsSection />
          </div>
        )}
      </div>
    </ReferralPageContainer>
  );
};

export default ReferralPage;
